import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation as useRouterLocation } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Trash2, Plus, Upload, Download, CheckCircle, XCircle, AlertCircle, RefreshCw, Merge, FileSpreadsheet, RotateCcw, X, Search, Package, Loader2, Printer } from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/use-auth';
import type { ProductInsert } from '@/integrations/supabase/types';
import { productService } from '@/lib/packages';
import type { Database } from '@/integrations/supabase/types';
import { logActivity } from '@/lib/activity-log';
import { useLocation as useLocationContext } from '@/contexts/LocationContext';
import { useProfitSettings } from '@/hooks/use-profit-settings';
import { getMarsPrice, type MarsPriceTier } from '@/lib/mars-prices';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info } from 'lucide-react';

type ProductRow = Database['public']['Tables']['products']['Row'];

interface StockItem {
  id: string;
  productCode: string;
  productName: string;
  category: string;
  cost: string;
  quantity: string;
  errors?: string[];
  isExisting?: boolean;
  wasMerged?: boolean;
  originalRowCount?: number;
  searchTerm?: string; // For tracking search input per row (product code)
  searchTermName?: string; // For tracking search input for product name
  searchTermCategory?: string; // For tracking search input for category
  showSearchResults?: boolean; // For showing/hiding search results per row (product code)
  showSearchResultsName?: boolean; // For showing/hiding search results for product name
  showSearchResultsCategory?: boolean; // For showing/hiding search results for category
  selectedProductId?: string; // Track which product is selected to prevent manual edits
  marsPriceSuggestion?: {
    price: number;
    tier: string;
    priceDate: string;
  } | null; // Mars price suggestion for this item
  sourceEirsaliyeItemId?: string;
  sourceEirsaliyeNo?: string;
  costSource?:
    | 'mars_product_prices.pesin_price'
    | 'mars_product_prices.price_30'
    | 'mars_product_prices.price_60'
    | 'mars_product_prices.price_90'
    | 'mars_product_prices.price_120'
    | 'mars_product_prices.price_150'
    | 'mars_product_prices.price_180'
    | 'products.current_cost'
    | 'products.initial_cost'
    | 'fallback_zero';
  costSourceLabel?: string;
}

interface ValidationError {
  row: number;
  field: string;
  message: string;
}

interface StockComparison {
  productCode: string;
  productName: string;
  category: string;
  currentQuantity: number;
  uploadedQuantity: number;
  quantityChange: number;
  currentCost: number | null;
  uploadedCost: number | null;
  productId?: string; // For existing products
  isNew: boolean;
  willBeRemoved?: boolean; // For full wipe mode - products in DB but not in Excel
}

interface EirsaliyeImportLocationItem {
  eirsaliye_item_id: string;
  irsaliye_no: string;
  product_code: string;
  product_name: string;
  category: string;
  quantity: number;
  cost: number;
  cost_source:
    | 'mars_product_prices.pesin_price'
    | 'mars_product_prices.price_30'
    | 'mars_product_prices.price_60'
    | 'mars_product_prices.price_90'
    | 'mars_product_prices.price_120'
    | 'mars_product_prices.price_150'
    | 'mars_product_prices.price_180'
    | 'products.current_cost'
    | 'products.initial_cost'
    | 'fallback_zero';
  cost_source_label: string;
  matched_product_id: string | null;
}

interface StockEntryLocationState {
  annualInventoryMatchedItems?: StockItem[];
  fromAnnualInventoryReview?: boolean;
  fromEirsaliyeImport?: boolean;
  eirsaliyeDate?: string;
  eirsaliyeImportItems?: EirsaliyeImportLocationItem[];
}

const StockEntry = () => {
  const { user, refreshSession, isSessionValid, companyId } = useAuth();
  const { currentLocation } = useLocationContext();
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  const { settings: profitSettings } = useProfitSettings();
  
  const [stockItems, setStockItems] = useState<StockItem[]>([
    {
      id: '1',
      productCode: '',
      productName: '',
      category: '',
      cost: '',
      quantity: '',
      searchTerm: '',
      searchTermName: '',
      searchTermCategory: '',
      showSearchResults: false,
      showSearchResultsName: false,
      showSearchResultsCategory: false
    }
  ]);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [validatingItems, setValidatingItems] = useState(false);
  const [validationSummary, setValidationSummary] = useState({
    total: 0,
    valid: 0,
    errors: 0,
    existing: 0,
    newProducts: 0
  });
  const [excelUploadMode, setExcelUploadMode] = useState<'update' | 'clean_slate'>('update');
  const [showModeSelection, setShowModeSelection] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showProgress, setShowProgress] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [uploadReport, setUploadReport] = useState({
    total: 0,
    created: 0,
    updated: 0,
    errors: 0,
    createdItems: [] as StockItem[],
    updatedItems: [] as StockItem[],
    errorItems: [] as { item: StockItem; error: string }[]
  });
  const [showComparison, setShowComparison] = useState(false);
  const [stockComparison, setStockComparison] = useState<StockComparison[]>([]);
  const [pendingExcelItems, setPendingExcelItems] = useState<StockItem[]>([]);
  const [eirsaliyeImportDate, setEirsaliyeImportDate] = useState<string | null>(null);

  // Product search state per row
  const [productSearchResults, setProductSearchResults] = useState<Map<string, ProductRow[]>>(new Map());
  const [productSearchLoading, setProductSearchLoading] = useState<Map<string, boolean>>(new Map());
  const searchTimeoutRefs = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const searchInputRefs = useRef<Map<string, HTMLInputElement | null>>(new Map());
  const searchNameInputRefs = useRef<Map<string, HTMLInputElement | null>>(new Map());
  const searchCategoryInputRefs = useRef<Map<string, HTMLInputElement | null>>(new Map());
  
  // Category search state
  const [categorySearchResults, setCategorySearchResults] = useState<Map<string, string[]>>(new Map());
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [categorySearchLoading, setCategorySearchLoading] = useState<Map<string, boolean>>(new Map());
  
  // Dropdown position state for portal rendering
  const [dropdownPositions, setDropdownPositions] = useState<Map<string, { top: number; left: number; width: number }>>(new Map());
  
  // Dialog state for new product code input
  const [showNewProductDialog, setShowNewProductDialog] = useState(false);
  const [newProductCode, setNewProductCode] = useState('');
  const [pendingNewProduct, setPendingNewProduct] = useState<{ itemId: string; productName: string; category: string } | null>(null);






  const addNewRow = () => {
    const newItem: StockItem = {
      id: Date.now().toString(),
      productCode: '',
      productName: '',
      category: '',
      cost: '',
      quantity: '',
      searchTerm: '',
      searchTermName: '',
      searchTermCategory: '',
      showSearchResults: false,
      showSearchResultsName: false,
      showSearchResultsCategory: false
    };
    setStockItems([...stockItems, newItem]);
  };

  const removeRow = (id: string) => {
    if (stockItems.length > 1) {
      setStockItems(stockItems.filter(item => item.id !== id));
    }
  };

  const updateItem = (id: string, field: keyof StockItem, value: string) => {
    setStockItems(stockItems.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  // Fetch unique categories from database
  const fetchAvailableCategories = useCallback(async () => {
    if (!companyId) return;
    
    try {
      const { data, error } = await supabase
        .from('products')
        .select('category')
        .eq('company_id', companyId)
        .not('category', 'is', null);
      
      if (error) {
        console.error('Error fetching categories:', error);
        return;
      }
      
      // Get unique categories, filter out empty strings, and sort
      const uniqueCategories = Array.from(new Set(
        (data || [])
          .map(p => p.category)
          .filter((cat): cat is string => Boolean(cat) && cat.trim() !== '')
      )).sort();
      
      setAvailableCategories(uniqueCategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, [companyId]);

  // Fetch categories when companyId is available
  useEffect(() => {
    void fetchAvailableCategories();
  }, [fetchAvailableCategories]);

  // Handle navigation state payloads (annual inventory review / e-Irsaliye import)
  useEffect(() => {
    const navState = routerLocation.state as StockEntryLocationState | null;

    if (navState?.fromEirsaliyeImport && navState.eirsaliyeImportItems?.length) {
      const importedItems: StockItem[] = navState.eirsaliyeImportItems.map((item, index) => ({
        id: `${Date.now()}-${index}`,
        productCode: item.product_code || '',
        productName: item.product_name || '',
        category: item.category || '',
        cost: String(item.cost ?? 0),
        quantity: String(item.quantity ?? 0),
        searchTerm: '',
        searchTermName: '',
        searchTermCategory: '',
        showSearchResults: false,
        showSearchResultsName: false,
        showSearchResultsCategory: false,
        selectedProductId: item.matched_product_id || undefined,
        sourceEirsaliyeItemId: item.eirsaliye_item_id,
        sourceEirsaliyeNo: item.irsaliye_no,
        costSource: item.cost_source,
        costSourceLabel: item.cost_source_label,
      }));

      if (importedItems.length > 0) {
        setStockItems(importedItems);
        setShowPreview(false);
        setShowComparison(false);
        setPendingExcelItems([]);
        setStockComparison([]);
        setEirsaliyeImportDate(null);
        setValidationSummary({
          total: 0,
          valid: 0,
          errors: 0,
          existing: 0,
          newProducts: 0,
        });
        setEirsaliyeImportDate(navState.eirsaliyeDate || null);
        toast.success(`${importedItems.length} kalem e-Irsaliyeden stok girisine aktarildi`);
      }

      window.history.replaceState({}, document.title);
      return;
    }

    if (navState?.fromAnnualInventoryReview && navState.annualInventoryMatchedItems) {
      // Process the matched items from review page
      const processReturn = async () => {
        await handleFinishAnnualInventoryReview(navState.annualInventoryMatchedItems!);
        // Clear location state
        window.history.replaceState({}, document.title);
      };
      void processReturn();
    }
  }, [routerLocation.state]);

  // Search products function - can search by name, code, or category
  const searchProducts = useCallback(async (itemId: string, searchTerm: string, searchType: 'code' | 'name' | 'category' = 'code') => {
    if (!companyId || searchTerm.length < 2) {
      setProductSearchResults(prev => {
        const newMap = new Map(prev);
        newMap.set(itemId, []);
        return newMap;
      });
      setProductSearchLoading(prev => {
        const newMap = new Map(prev);
        newMap.set(itemId, false);
        return newMap;
      });
      return;
    }

    setProductSearchLoading(prev => {
      const newMap = new Map(prev);
      newMap.set(itemId, true);
      return newMap;
    });

    try {
      let products: ProductRow[] = [];
      
      if (searchType === 'category') {
        // Search by category - get all products and filter by category
        const allProducts = await productService.getAllProducts(companyId, {
          showAllLocations: true,
          includeInactive: false
        });
        products = allProducts
          .filter(p => p.category?.toLowerCase().includes(searchTerm.toLowerCase()))
          .slice(0, 10);
      } else {
        // Search by name or code using existing search function
        products = await productService.searchProducts(companyId, searchTerm, 10);
      }
      
      setProductSearchResults(prev => {
        const newMap = new Map(prev);
        newMap.set(itemId, products);
        return newMap;
      });
    } catch (error) {
      console.error('Error searching products:', error);
      setProductSearchResults(prev => {
        const newMap = new Map(prev);
        newMap.set(itemId, []);
        return newMap;
      });
    } finally {
      setProductSearchLoading(prev => {
        const newMap = new Map(prev);
        newMap.set(itemId, false);
        return newMap;
      });
    }
  }, [companyId]);

  // Handle product search input change for product code
  const handleProductSearchChange = (itemId: string, value: string) => {
    // Update the search term and productCode to keep them in sync
    setStockItems(prev => prev.map(item => 
      item.id === itemId 
        ? { 
            ...item, 
            searchTerm: value, 
            productCode: value, // Always update productCode to keep it in sync
            showSearchResults: value.length >= 2, 
            selectedProductId: undefined 
          }
        : item
    ));

    // Clear existing timeout
    const existingTimeout = searchTimeoutRefs.current.get(itemId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Set new timeout for debounced search
    if (value.length >= 2) {
      const timeout = setTimeout(() => {
        searchProducts(itemId, value, 'code');
      }, 300);
      searchTimeoutRefs.current.set(itemId, timeout);
    } else {
      setProductSearchResults(prev => {
        const newMap = new Map(prev);
        newMap.set(itemId, []);
        return newMap;
      });
    }
  };

  // Handle product name search input change
  const handleProductNameSearchChange = (itemId: string, value: string) => {
    setStockItems(prev => prev.map(item => 
      item.id === itemId 
        ? { 
            ...item, 
            searchTermName: value, 
            productName: value, // Always update productName to keep it in sync
            showSearchResultsName: value.length >= 2, 
            selectedProductId: undefined 
          }
        : item
    ));

    const existingTimeout = searchTimeoutRefs.current.get(`${itemId}-name`);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    if (value.length >= 2) {
      const timeout = setTimeout(() => {
        searchProducts(itemId, value, 'name');
      }, 300);
      searchTimeoutRefs.current.set(`${itemId}-name`, timeout);
    } else {
      setProductSearchResults(prev => {
        const newMap = new Map(prev);
        newMap.set(itemId, []);
        return newMap;
      });
    }
  };

  // Handle category search input change - search categories instead of products
  const handleCategorySearchChange = (itemId: string, value: string) => {
    setStockItems(prev => prev.map(item => 
      item.id === itemId 
        ? { 
            ...item, 
            searchTermCategory: value, 
            category: value, // Always update category to keep it in sync
            // Keep dropdown open if we have categories to show (handled by onFocus or if already open)
            showSearchResultsCategory: item.showSearchResultsCategory || availableCategories.length > 0,
            selectedProductId: undefined 
          }
        : item
    ));

    const existingTimeout = searchTimeoutRefs.current.get(`${itemId}-category`);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Filter categories based on search term, or show all if empty
    if (value.length >= 1) {
      const timeout = setTimeout(() => {
        const filteredCategories = availableCategories.filter(cat =>
          cat.toLowerCase().includes(value.toLowerCase())
        ); // Show all matching categories, no limit
        
        setCategorySearchResults(prev => {
          const newMap = new Map(prev);
          newMap.set(itemId, filteredCategories);
          return newMap;
        });
        
        setCategorySearchLoading(prev => {
          const newMap = new Map(prev);
          newMap.set(itemId, false);
          return newMap;
        });
      }, 150); // Shorter delay for category search
      searchTimeoutRefs.current.set(`${itemId}-category`, timeout);
    } else {
      // Show all categories when search term is empty
      setCategorySearchResults(prev => {
        const newMap = new Map(prev);
        newMap.set(itemId, availableCategories);
        return newMap;
      });
    }
  };

  // Handle category selection
  const handleCategorySelect = (itemId: string, category: string, event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    setStockItems(prev => prev.map(item => 
      item.id === itemId 
        ? {
            ...item,
            category: category,
            searchTermCategory: '',
            showSearchResultsCategory: false
          }
        : item
    ));

    // Clear search results for this item
    setCategorySearchResults(prev => {
      const newMap = new Map(prev);
      newMap.set(itemId, []);
      return newMap;
    });
  };

  // Check Mars price for a product
  const checkMarsPrice = async (sku: string | null | undefined, tier: MarsPriceTier): Promise<{ price: number; priceDate: string } | null> => {
    if (!sku) return null;

    try {
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      
      // Get the first and last day of current month
      const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
      const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
      
      // Query for Mars prices in the current month
      const { data, error } = await supabase
        .from('mars_product_prices')
        .select('*')
        .eq('sku', sku)
        .gte('price_date', firstDayOfMonth.toISOString().split('T')[0])
        .lte('price_date', lastDayOfMonth.toISOString().split('T')[0])
        .order('price_date', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error || !data) {
        return null;
      }

      // Get the price based on tier
      const priceMap: Record<MarsPriceTier, keyof typeof data> = {
        pesin: 'pesin_price',
        '30': 'price_30',
        '60': 'price_60',
        '90': 'price_90',
        '120': 'price_120',
        '150': 'price_150',
        '180': 'price_180',
      };

      const priceKey = priceMap[tier];
      const price = data[priceKey];

      if (!price) {
        return null;
      }

      return {
        price: parseFloat(price.toString()),
        priceDate: data.price_date,
      };
    } catch (error) {
      console.error('Error checking Mars price:', error);
      return null;
    }
  };

  // Handle product selection from search results - fills all three fields
  const handleProductSelect = async (itemId: string, product: ProductRow, searchType: 'code' | 'name' | 'category' = 'code', event?: React.MouseEvent) => {
    // Prevent event propagation to avoid closing dropdown prematurely
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Check for Mars price suggestion
    let marsPriceSuggestion: { price: number; tier: string; priceDate: string } | null = null;
    if (product.item_code && profitSettings.marsPriceTier) {
      const marsPrice = await checkMarsPrice(product.item_code, profitSettings.marsPriceTier);
      if (marsPrice) {
        marsPriceSuggestion = {
          price: marsPrice.price,
          tier: profitSettings.marsPriceTier,
          priceDate: marsPrice.priceDate,
        };
      }
    }

    setStockItems(prev => prev.map(item => 
      item.id === itemId 
        ? {
            ...item,
            productCode: product.item_code || '',
            productName: product.name || '',
            category: product.category || '',
            cost: (product.current_cost || product.initial_cost || 0).toString(),
            searchTerm: '',
            searchTermName: '',
            searchTermCategory: '',
            showSearchResults: false,
            showSearchResultsName: false,
            showSearchResultsCategory: false,
            selectedProductId: product.id, // Mark which product is selected
            marsPriceSuggestion: marsPriceSuggestion
          }
        : item
    ));

    // Clear search results for this item
    setProductSearchResults(prev => {
      const newMap = new Map(prev);
      newMap.set(itemId, []);
      return newMap;
    });
  };

  // Handle using Mars price suggestion
  const handleUseMarsPrice = (itemId: string) => {
    setStockItems(prev => prev.map(item => {
      if (item.id === itemId && item.marsPriceSuggestion) {
        return {
          ...item,
          cost: item.marsPriceSuggestion.price.toString(),
          marsPriceSuggestion: null, // Clear suggestion after using it
        };
      }
      return item;
    }));
  };

  // Handle manual field changes - warn if trying to edit a selected product
  const handleFieldChange = async (itemId: string, field: 'productCode' | 'productName' | 'category', value: string) => {
    setStockItems(prev => prev.map(item => {
      if (item.id === itemId) {
        // If product is selected, clear the selection when manually editing
        const updatedItem = { 
          ...item, 
          [field]: value,
          selectedProductId: undefined, // Clear selection when manually editing
          marsPriceSuggestion: undefined // Clear Mars price suggestion when manually editing
        };
        
        // Clear search terms when manually editing
        if (field === 'productCode') {
          updatedItem.searchTerm = value;
          
          // Check Mars price if product code is entered and we have a tier selected
          if (value && profitSettings.marsPriceTier) {
            // Check Mars price asynchronously
            checkMarsPrice(value, profitSettings.marsPriceTier).then(marsPrice => {
              if (marsPrice) {
                setStockItems(prevItems => prevItems.map(prevItem => {
                  if (prevItem.id === itemId && prevItem.productCode === value) {
                    return {
                      ...prevItem,
                      marsPriceSuggestion: {
                        price: marsPrice.price,
                        tier: profitSettings.marsPriceTier!,
                        priceDate: marsPrice.priceDate,
                      },
                    };
                  }
                  return prevItem;
                }));
              }
            }).catch(err => {
              console.error('Error checking Mars price:', err);
            });
          }
        } else if (field === 'productName') {
          updatedItem.searchTermName = value;
        } else if (field === 'category') {
          updatedItem.searchTermCategory = value;
        }
        
        return updatedItem;
      }
      return item;
    }));
  };

  // Handle click outside to close search results
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Check if click is on a dropdown item (prevent closing)
      const isDropdownItem = (target as HTMLElement).closest('[data-dropdown-item]');
      if (isDropdownItem) {
        return; // Don't close if clicking on dropdown item
      }
      
      // Close product code search results
      searchInputRefs.current.forEach((ref, itemId) => {
        if (ref && !ref.contains(target)) {
          setStockItems(prev => prev.map(item => 
            item.id === itemId ? { ...item, showSearchResults: false } : item
          ));
        }
      });
      
      // Close product name search results
      searchNameInputRefs.current.forEach((ref, itemId) => {
        if (ref && !ref.contains(target)) {
          setStockItems(prev => prev.map(item => 
            item.id === itemId ? { ...item, showSearchResultsName: false } : item
          ));
        }
      });
      
      // Close category search results
      searchCategoryInputRefs.current.forEach((ref, itemId) => {
        if (ref && !ref.contains(target)) {
          setStockItems(prev => prev.map(item => 
            item.id === itemId ? { ...item, showSearchResultsCategory: false } : item
          ));
          // Clear category search results when closing
          setCategorySearchResults(prev => {
            const newMap = new Map(prev);
            newMap.set(itemId, []);
            return newMap;
          });
        }
      });
    };

    // Use a slight delay to allow click events to process first
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      searchTimeoutRefs.current.forEach(timeout => clearTimeout(timeout));
    };
  }, []);

  // Handle new product creation - prompt for ürün kodu
  const handleNewProductPrompt = (itemId: string, productName: string, category: string) => {
    setPendingNewProduct({ itemId, productName, category });
    setNewProductCode('');
    setShowNewProductDialog(true);
  };

  const handleConfirmNewProduct = async () => {
    if (!pendingNewProduct || !newProductCode.trim()) {
      toast.error('Ürün kodu boş olamaz');
      return;
    }

    const codeToSave = newProductCode.trim();
    const savedPending = pendingNewProduct;

    // Update the item with the new product code and retry saving
    setStockItems(prev => {
      const updatedItems = prev.map(item => 
        item.id === savedPending.itemId 
          ? {
              ...item,
              productCode: codeToSave,
              productName: savedPending.productName,
              category: savedPending.category,
              selectedProductId: undefined // New product, not selected from existing
            }
          : item
      );
      
      // Retry saving after code is provided - allow 0 quantity
      const validItems = updatedItems.filter(item => {
        const hasProductCode = item.productCode && item.productCode.trim() !== '';
        const hasProductName = item.productName && item.productName.trim() !== '';
        const hasQuantity = item.quantity !== '' && item.quantity !== null && item.quantity !== undefined;
        const isValidQuantity = hasQuantity && !isNaN(parseInt(item.quantity)) && parseInt(item.quantity) >= 0;
        
        return hasProductCode && hasProductName && isValidQuantity;
      });
      
      if (validItems.length > 0) {
        // Use setTimeout to ensure state is updated before calling saveItemsToDatabase
        setTimeout(() => {
          saveItemsToDatabase(validItems);
        }, 0);
      }
      
      return updatedItems;
    });

    setShowNewProductDialog(false);
    setPendingNewProduct(null);
    setNewProductCode('');
  };

  const validateItem = (item: StockItem): string[] => {
    const errors: string[] = [];
    
    if (!item.productCode.trim()) {
      errors.push('Ürün kodu boş olamaz');
    }
    
    if (!item.productName.trim()) {
      errors.push('Ürün adı boş olamaz');
    }
    
    if (item.quantity.trim() === '') {
      errors.push('Adet boş olamaz');
    } else {
      const qtyNum = parseInt(item.quantity);
      if (isNaN(qtyNum) || qtyNum < 0) {
        errors.push('Adet 0 veya pozitif bir sayı olmalıdır');
      }
      // Explicitly allow 0 - no error if qtyNum === 0
    }
    
    if (item.cost.trim() && (isNaN(parseFloat(item.cost)) || parseFloat(item.cost) < 0)) {
      errors.push('Maliyet 0 veya pozitif bir sayı olmalıdır');
    }
    
    return errors;
  };

  const validateAndCheckExisting = async (items: StockItem[]) => {
    if (!companyId) return;
    
    setValidatingItems(true);
    
    try {
      // Get all existing product codes for this company
      const { data: existingProducts } = await supabase
        .from('products')
        .select('item_code, id, name')
        .eq('company_id', companyId);
      
      const existingCodes = new Set(existingProducts?.map(p => p.item_code) || []);
      
      const validatedItems = items.map(item => {
        const errors = validateItem(item);
        const isExisting = existingCodes.has(item.productCode);
        
        return {
          ...item,
          errors,
          isExisting
        };
      });
      
      const summary = {
        total: validatedItems.length,
        valid: validatedItems.filter(item => !item.errors || item.errors.length === 0).length,
        errors: validatedItems.filter(item => item.errors && item.errors.length > 0).length,
        existing: validatedItems.filter(item => item.isExisting && (!item.errors || item.errors.length === 0)).length,
        newProducts: validatedItems.filter(item => !item.isExisting && (!item.errors || item.errors.length === 0)).length
      };
      
      setStockItems(validatedItems);
      setValidationSummary(summary);
      setShowPreview(true);
      
    } catch (error) {
      console.error('Error validating items:', error);
      toast.error('Doğrulama sırasında hata oluştu');
    } finally {
      setValidatingItems(false);
    }
  };

  const mergeDuplicateProducts = (items: StockItem[]): StockItem[] => {
    const merged = new Map<string, StockItem>();
    const rowCounts = new Map<string, number>();
    
    items.forEach(item => {
      const productCode = item.productCode.toLowerCase();
      
      // Count how many times we see this product code
      rowCounts.set(productCode, (rowCounts.get(productCode) || 0) + 1);
      
      if (merged.has(productCode)) {
        const existing = merged.get(productCode)!;
        const existingQty = parseInt(existing.quantity) || 0;
        const newQty = parseInt(item.quantity) || 0;
        const totalQty = existingQty + newQty;
        
        // Update the existing item with merged data
        merged.set(productCode, {
          ...existing,
          quantity: totalQty.toString(),
          // Use the most recent cost if provided, otherwise keep existing
          cost: item.cost.trim() ? item.cost : existing.cost,
          // Use the most recent category if provided, otherwise keep existing
          category: item.category.trim() ? item.category : existing.category,
          // Use the most recent product name if provided, otherwise keep existing
          productName: item.productName.trim() ? item.productName : existing.productName,
          wasMerged: true,
          originalRowCount: rowCounts.get(productCode),
          // Preserve search fields
          searchTerm: existing.searchTerm || '',
          searchTermName: existing.searchTermName || '',
          searchTermCategory: existing.searchTermCategory || '',
          showSearchResults: false,
          showSearchResultsName: false,
          showSearchResultsCategory: false
        });
      } else {
        merged.set(productCode, { 
          ...item,
          wasMerged: false,
          originalRowCount: 1,
          // Ensure search fields are set
          searchTerm: item.searchTerm || '',
          searchTermName: item.searchTermName || '',
          searchTermCategory: item.searchTermCategory || '',
          showSearchResults: false,
          showSearchResultsName: false,
          showSearchResultsCategory: false
        });
      }
    });
    
    // Update wasMerged flag for items that actually had duplicates
    rowCounts.forEach((count, productCode) => {
      if (count > 1 && merged.has(productCode)) {
        const item = merged.get(productCode)!;
        merged.set(productCode, {
          ...item,
          wasMerged: true,
          originalRowCount: count
        });
      }
    });
    
    return Array.from(merged.values());
  };

  const compareWithCurrentStock = async (excelItems: StockItem[], mode: 'update' | 'clean_slate'): Promise<StockComparison[]> => {
    if (!companyId) return [];

    try {
      // Fetch all current products with their stock
      const { data: currentProducts, error } = await supabase
        .from('products')
        .select('id, item_code, name, category, quantity_on_hand, current_cost')
        .eq('company_id', companyId);

      if (error) {
        console.error('Error fetching current stock:', error);
        toast.error('Mevcut stok bilgileri alınamadı');
        return [];
      }

      const comparisons: StockComparison[] = [];
      const currentStockMap = new Map<string, {
        id: string;
        name: string;
        category: string | null;
        quantity: number;
        cost: number | null;
      }>();

      // Build map of current stock
      currentProducts?.forEach(product => {
        if (product.item_code) {
          currentStockMap.set(product.item_code.toLowerCase(), {
            id: product.id,
            name: product.name || '',
            category: product.category,
            quantity: product.quantity_on_hand || 0,
            cost: product.current_cost
          });
        }
      });

      // Compare Excel items with current stock
      excelItems.forEach(item => {
        const productCode = item.productCode.toLowerCase();
        const currentStock = currentStockMap.get(productCode);
        const uploadedQty = parseInt(item.quantity) || 0;
        const uploadedCost = parseFloat(item.cost) || null;

        if (currentStock) {
          // Existing product
          let finalQuantity: number;
          let quantityChange: number;
          
          if (mode === 'update') {
            // In update mode: add uploaded quantity to current
            finalQuantity = currentStock.quantity + uploadedQty;
            quantityChange = uploadedQty; // Amount to add
          } else {
            // In full wipe mode: replace with uploaded quantity
            finalQuantity = uploadedQty;
            quantityChange = uploadedQty - currentStock.quantity; // Difference
          }

          comparisons.push({
            productCode: item.productCode,
            productName: item.productName || currentStock.name,
            category: item.category || currentStock.category || '',
            currentQuantity: currentStock.quantity,
            uploadedQuantity: finalQuantity, // Show final quantity after operation
            quantityChange: quantityChange,
            currentCost: currentStock.cost,
            uploadedCost: uploadedCost,
            productId: currentStock.id,
            isNew: false
          });
        } else {
          // New product
          comparisons.push({
            productCode: item.productCode,
            productName: item.productName,
            category: item.category,
            currentQuantity: 0,
            uploadedQuantity: uploadedQty,
            quantityChange: uploadedQty,
            currentCost: null,
            uploadedCost: uploadedCost,
            isNew: true
          });
        }
      });

      // For full wipe mode, add products that exist in DB but not in Excel (will be set to 0)
      if (mode === 'clean_slate') {
        const excelCodes = new Set(excelItems.map(item => item.productCode.toLowerCase()));
        currentProducts?.forEach(product => {
          if (product.item_code && !excelCodes.has(product.item_code.toLowerCase())) {
            comparisons.push({
              productCode: product.item_code,
              productName: product.name || '',
              category: product.category || '',
              currentQuantity: product.quantity_on_hand || 0,
              uploadedQuantity: 0,
              quantityChange: -(product.quantity_on_hand || 0),
              currentCost: product.current_cost,
              uploadedCost: null,
              productId: product.id,
              isNew: false,
              willBeRemoved: true
            });
          }
        });
      }

      return comparisons;
    } catch (error) {
      console.error('Error comparing stock:', error);
      toast.error('Stok karşılaştırması yapılırken hata oluştu');
      return [];
    }
  };

  const handleExcelUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as string[][];

        // Skip header row and convert to stock items
        const rawItems: StockItem[] = jsonData.slice(1).map((row, index) => ({
          id: (Date.now() + index).toString(),
          productCode: (row[0] || '').toString().trim(),
          productName: (row[1] || '').toString().trim(),
          category: (row[2] || '').toString().trim(),
          cost: (row[3] !== undefined && row[3] !== null) ? row[3].toString().trim() : '',
          quantity: (row[4] !== undefined && row[4] !== null) ? row[4].toString().trim() : '',
          searchTerm: '',
          searchTermName: '',
          searchTermCategory: '',
          showSearchResults: false,
          showSearchResultsName: false,
          showSearchResultsCategory: false
        })).filter(item => item.productCode || item.productName);

        // Merge duplicate product codes by summing quantities
        const mergedItems = mergeDuplicateProducts(rawItems);

        if (mergedItems.length > 0) {
          // Validate items first
          const validatedItems = await validateItems(mergedItems);
          
          // Compare with current stock
          const comparison = await compareWithCurrentStock(validatedItems, excelUploadMode);
          
          // Store pending items and show comparison
          setPendingExcelItems(validatedItems);
          setStockComparison(comparison);
          setShowComparison(true);

          const duplicateCount = rawItems.length - mergedItems.length;
          const modeText = excelUploadMode === 'update' ? 'güncelleme' : 'tam değiştirme';
          toast.success(`${mergedItems.length} ürün yüklendi${duplicateCount > 0 ? ` (${duplicateCount} duplikat birleştirildi)` : ''} - ${modeText} modu`);
        } else {
          toast.error('Excel dosyasında geçerli veri bulunamadı');
        }
      } catch (error) {
        console.error('Excel upload error:', error);
        toast.error('Excel dosyası okunurken hata oluştu');
      }
    };
    reader.readAsArrayBuffer(file);
    event.target.value = '';
    setShowModeSelection(false);
  };

  const validateItems = async (items: StockItem[]): Promise<StockItem[]> => {
    if (!companyId) return items;

    try {
      // Get all existing product codes for this company
      const { data: existingProducts } = await supabase
        .from('products')
        .select('item_code, id, name')
        .eq('company_id', companyId);
      
      const existingCodes = new Set(existingProducts?.map(p => p.item_code) || []);
      
      return items.map(item => {
        const errors = validateItem(item);
        const isExisting = existingCodes.has(item.productCode);
        
        return {
          ...item,
          errors,
          isExisting
        };
      });
    } catch (error) {
      console.error('Error validating items:', error);
      return items;
    }
  };

  const downloadTemplate = () => {
    const templateData = [
      ['Ürün Kodu', 'Ürün Adı', 'Kategori', 'Maliyet', 'Adet'],
      ['PROD001', 'Örnek Ürün 1', 'Kategori A', '100.00', '10'],
      ['PROD002', 'Örnek Ürün 2', 'Kategori B', '200.00', '5'],
      ['PROD003', 'Sıfır Stok Ürün', 'Kategori C', '0', '0'],
      ['PROD004', 'Eksi Stok Ürün', 'Kategori D', '150.00', '-2']
    ];

    const ws = XLSX.utils.aoa_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Stok Şablonu');
    XLSX.writeFile(wb, 'stok_girisi_sablonu.xlsx');
  };

  const downloadAnnualInventoryTemplate = async () => {
    if (!companyId) {
      toast.error('Şirket bilgisi bulunamadı');
      return;
    }

    try {
      toast.info('Ürün listesi yükleniyor...');
      
      // Fetch all products with their current cost
      const { data: products, error } = await supabase
        .from('products')
        .select('item_code, name, current_cost')
        .eq('company_id', companyId)
        .not('item_code', 'is', null)
        .order('item_code');

      if (error) {
        console.error('Error fetching products:', error);
        toast.error('Ürünler yüklenirken hata oluştu');
        return;
      }

      if (!products || products.length === 0) {
        toast.error('Hiç ürün bulunamadı');
        return;
      }

      // Create template data with header
      const templateData = [
        ['Ürün Kodu', 'Ürün Adı', 'Mevcut Maliyet']
      ];

      // Add product rows
      products.forEach(product => {
        const cost = product.current_cost || 0;
        templateData.push([
          product.item_code || '',
          product.name || '',
          cost.toString()
        ]);
      });

      const ws = XLSX.utils.aoa_to_sheet(templateData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Yıllık Sayım Şablonu');
      XLSX.writeFile(wb, 'yillik_sayim_sablonu.xlsx');
      
      toast.success(`${products.length} ürün içeren şablon indirildi`);
    } catch (error) {
      console.error('Error downloading annual inventory template:', error);
      toast.error('Şablon indirilirken hata oluştu');
    }
  };

  // Finish review and proceed to comparison (called when returning from review page)
  const handleFinishAnnualInventoryReview = async (finalMatchedItems: StockItem[]) => {
    // Validate all items
    const validatedItems = await validateItems(finalMatchedItems);
    
    // Compare with current stock
    const comparison = await compareWithCurrentStock(validatedItems, 'update');
    
    // Navigate to comparison page
    navigate('/annual-inventory-comparison', {
      state: {
        comparison: comparison,
        excelItems: validatedItems,
        mode: 'update' as const
      }
    });

    toast.success('Tüm ürünler işlendi, karşılaştırma sayfasına yönlendiriliyorsunuz...');
  };

  const handleAnnualInventoryExcelUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as string[][];

        // Skip header row
        const rows = jsonData.slice(1);
        
        // Count lines per product code
        const productCountMap = new Map<string, {
          productCode: string;
          productName: string;
          cost: number;
          quantity: number;
        }>();

        rows.forEach((row, index) => {
          const productCode = (row[0] || '').toString().trim();
          const productName = (row[1] || '').toString().trim();
          const costStr = (row[2] || '').toString().trim();

          if (!productCode) {
            return; // Skip empty rows
          }

          const cost = costStr ? parseFloat(costStr) : 0;
          const key = productCode.toLowerCase();

          if (productCountMap.has(key)) {
            // Increment quantity (each line = 1 item)
            const existing = productCountMap.get(key)!;
            existing.quantity += 1;
            // Use the cost from the first occurrence (all should be same)
            if (cost > 0 && existing.cost === 0) {
              existing.cost = cost;
            }
          } else {
            // First occurrence of this product code
            productCountMap.set(key, {
              productCode,
              productName,
              cost: cost || 0,
              quantity: 1
            });
          }
        });

        // Convert map to StockItem array
        const stockItems: StockItem[] = Array.from(productCountMap.values()).map((item, index) => ({
          id: (Date.now() + index).toString(),
          productCode: item.productCode,
          productName: item.productName,
          category: '', // Will be filled from database if product exists
          cost: item.cost.toString(),
          quantity: item.quantity.toString(),
          searchTerm: '',
          searchTermName: '',
          searchTermCategory: '',
          showSearchResults: false,
          showSearchResultsName: false,
          showSearchResultsCategory: false
        }));

        if (stockItems.length > 0) {
          // Fetch product details from database to check which items match
          if (companyId) {
            const productCodes = stockItems.map(item => item.productCode);
            const { data: existingProducts } = await supabase
              .from('products')
              .select('item_code, name, category, current_cost')
              .eq('company_id', companyId)
              .in('item_code', productCodes);

            const productMap = new Map<string, { name: string; category: string; current_cost: number | null }>();
            existingProducts?.forEach(p => {
              if (p.item_code) {
                productMap.set(p.item_code.toLowerCase(), {
                  name: p.name || '',
                  category: p.category || '',
                  current_cost: p.current_cost
                });
              }
            });

            // Separate matched and unmatched items
            const matched: StockItem[] = [];
            const unmatched: StockItem[] = [];

            stockItems.forEach(item => {
              const dbProduct = productMap.get(item.productCode.toLowerCase());
              if (dbProduct) {
                // Matched - update with database info
                item.productName = dbProduct.name || item.productName;
                item.category = dbProduct.category || item.category;
                // If cost is 0 or empty in Excel but exists in DB, use DB cost
                const excelCost = parseFloat(item.cost) || 0;
                if (excelCost === 0 && dbProduct.current_cost) {
                  item.cost = dbProduct.current_cost.toString();
                } else if (excelCost > 0) {
                  // Use the cost from Excel if provided
                  item.cost = excelCost.toString();
                }
                matched.push(item);
              } else {
                // Unmatched - needs user review
                unmatched.push(item);
              }
            });

            // If there are unmatched items, navigate to review page
            if (unmatched.length > 0) {
              // Navigate to review page with unmatched and matched items
              navigate('/annual-inventory-review', {
                state: {
                  unmatchedItems: unmatched,
                  matchedItems: matched
                }
              });
              toast.info(`${matched.length} ürün eşleşti, ${unmatched.length} ürün için onay gerekiyor`);
            } else {
              // All items matched, proceed to comparison
              const validatedItems = await validateItems(matched);
              const comparison = await compareWithCurrentStock(validatedItems, 'update');
              setPendingExcelItems(validatedItems);
              setStockComparison(comparison);
              setExcelUploadMode('update');
              setShowComparison(true);
              toast.success(`${matched.length} ürün yüklendi (toplam ${rows.length} satır işlendi)`);
            }
          } else {
            toast.error('Şirket bilgisi bulunamadı');
          }
        } else {
          toast.error('Excel dosyasında geçerli veri bulunamadı');
        }
      } catch (error) {
        console.error('Annual inventory Excel upload error:', error);
        toast.error('Excel dosyası okunurken hata oluştu');
      }
    };
    reader.readAsArrayBuffer(file);
    event.target.value = '';
  };

  const handleApproveExcel = async () => {
    // Filter valid items from pending Excel items
    const validItems = pendingExcelItems.filter(item => {
      const hasNoErrors = !item.errors || item.errors.length === 0;
      const hasProductCode = item.productCode && item.productCode.trim() !== '';
      const hasProductName = item.productName && item.productName.trim() !== '';
      const hasQuantity = item.quantity !== '' && item.quantity !== null && item.quantity !== undefined;
      const isValidQuantity = hasQuantity && !isNaN(parseInt(item.quantity)) && parseInt(item.quantity) >= 0;
      
      return hasNoErrors && hasProductCode && hasProductName && isValidQuantity;
    });

    if (validItems.length === 0) {
      toast.error('Kaydedilecek geçerli ürün bulunamadı');
      return;
    }

    setShowComparison(false);
    await saveItemsToDatabase(validItems, excelUploadMode);
  };

  const handleRejectExcel = () => {
    setShowComparison(false);
    setPendingExcelItems([]);
    setStockComparison([]);
    toast.info('Excel yüklemesi iptal edildi');
  };

  const handleConfirmAndSave = async () => {
    // Filter valid items - allow 0 quantity, but check for validation errors
    const validItems = stockItems.filter(item => {
      const hasNoErrors = !item.errors || item.errors.length === 0;
      const hasProductCode = item.productCode && item.productCode.trim() !== '';
      const hasProductName = item.productName && item.productName.trim() !== '';
      const hasQuantity = item.quantity !== '' && item.quantity !== null && item.quantity !== undefined;
      const isValidQuantity = hasQuantity && !isNaN(parseInt(item.quantity)) && parseInt(item.quantity) >= 0;
      
      return hasNoErrors && hasProductCode && hasProductName && isValidQuantity;
    });

    if (validItems.length === 0) {
      toast.error('Kaydedilecek geçerli ürün bulunamadı');
      return;
    }

    await saveItemsToDatabase(validItems, 'update');
  };

  const handleSave = async () => {
    // If user exists but companyId is missing, try to wait a bit for it to load
    if (user && !companyId) {
      toast.info('Kullanıcı profili yükleniyor...');
      
      // Wait up to 5 seconds for companyId to be available
      let retries = 0;
      const maxRetries = 10; // 5 seconds total (500ms * 10)
      
      while (retries < maxRetries && !companyId) {
        await new Promise(resolve => setTimeout(resolve, 500));
        retries++;
        
        // Trigger a manual refresh if we're still waiting
        if (retries === 5) {
          try {
            await refreshSession();
          } catch (error) {
            console.error('Manual refresh failed:', error);
          }
        }
      }
    }
    
    if (!companyId || !user) {
      toast.error('Yetkilendirme hatası. Lütfen sayfayı yenileyin veya tekrar giriş yapın.');
      return;
    }

    // Filter valid items - allow 0 quantity
    const validItems = stockItems.filter(item => {
      const hasProductCode = item.productCode && item.productCode.trim() !== '';
      const hasProductName = item.productName && item.productName.trim() !== '';
      const hasQuantity = item.quantity !== '' && item.quantity !== null && item.quantity !== undefined;
      const isValidQuantity = hasQuantity && !isNaN(parseInt(item.quantity)) && parseInt(item.quantity) >= 0;
      
      return hasProductCode && hasProductName && isValidQuantity;
    });

    if (validItems.length === 0) {
      toast.error('En az bir geçerli ürün girişi yapmalısınız');
      return;
    }

    await saveItemsToDatabase(validItems, 'update');
  };

  const printComparison = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const modeText = excelUploadMode === 'update' ? 'Güncelleme Modu' : 'Tam Değiştirme Modu';
    const date = new Date().toLocaleString('tr-TR');
    
    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Stok Karşılaştırma Raporu</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { color: #333; border-bottom: 2px solid #333; padding-bottom: 10px; }
          h2 { color: #666; margin-top: 20px; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f2f2f2; font-weight: bold; }
          tr:nth-child(even) { background-color: #f9f9f9; }
          .new { background-color: #d4edda; }
          .updated { background-color: #fff3cd; }
          .removed { background-color: #f8d7da; }
          .increase { color: green; font-weight: bold; }
          .decrease { color: red; font-weight: bold; }
          .summary { margin: 20px 0; padding: 15px; background-color: #e7f3ff; border-left: 4px solid #2196F3; }
        </style>
      </head>
      <body>
        <h1>Stok Karşılaştırma Raporu</h1>
        <div class="summary">
          <p><strong>Mod:</strong> ${modeText}</p>
          <p><strong>Tarih:</strong> ${date}</p>
          <p><strong>Toplam Ürün:</strong> ${stockComparison.length}</p>
          <p><strong>Yeni Ürün:</strong> ${stockComparison.filter(c => c.isNew).length}</p>
          <p><strong>Güncellenecek:</strong> ${stockComparison.filter(c => !c.isNew && !c.willBeRemoved).length}</p>
          ${excelUploadMode === 'clean_slate' ? `<p><strong>Sıfırlanacak:</strong> ${stockComparison.filter(c => c.willBeRemoved).length}</p>` : ''}
        </div>
    `;

    // Major discrepancies (decreases in quantity)
    const majorDiscrepancies = stockComparison.filter(c => 
      !c.isNew && 
      !c.willBeRemoved && 
      c.quantityChange < 0 && 
      c.currentQuantity > 0
    );

    if (majorDiscrepancies.length > 0) {
      html += `<h2 style="color: #dc2626; margin-top: 30px;">⚠️ ÖNEMLİ UYARI: Stok Farkları Tespit Edildi (${majorDiscrepancies.length})</h2>`;
      html += `<div style="background-color: #fee2e2; border-left: 4px solid #dc2626; padding: 15px; margin: 20px 0;">
        <p><strong>Aşağıdaki ${majorDiscrepancies.length} üründe mevcut stoktan daha az miktar tespit edildi.</strong></p>
        <p>Bu durum stok kaybı veya sayım hatası gösterebilir. Lütfen kontrol ediniz.</p>
      </div>`;
      html += `<table style="margin-top: 10px;"><tr><th>Ürün Kodu</th><th>Ürün Adı</th><th>Mevcut Stok</th><th>Excel'deki Miktar</th><th>Fark (Eksik)</th></tr>`;
      majorDiscrepancies.forEach(c => {
        html += `<tr style="background-color: #fee2e2;">
          <td><strong>${c.productCode}</strong></td>
          <td>${c.productName}</td>
          <td style="color: #dc2626; font-weight: bold;">${c.currentQuantity} adet</td>
          <td style="color: #dc2626; font-weight: bold;">${c.uploadedQuantity} adet</td>
          <td style="color: #dc2626; font-weight: bold;">${c.quantityChange} adet</td>
        </tr>`;
      });
      html += `</table>`;
    }

    // New products
    const newProducts = stockComparison.filter(c => c.isNew);
    if (newProducts.length > 0) {
      html += `<h2>Yeni Ürünler (${newProducts.length})</h2>`;
      html += `<table><tr><th>Ürün Kodu</th><th>Ürün Adı</th><th>Kategori</th><th>Miktar</th><th>Maliyet</th></tr>`;
      newProducts.forEach(c => {
        html += `<tr class="new">
          <td>${c.productCode}</td>
          <td>${c.productName}</td>
          <td>${c.category}</td>
          <td>${c.uploadedQuantity}</td>
          <td>${c.uploadedCost ? '₺' + c.uploadedCost.toFixed(2) : '-'}</td>
        </tr>`;
      });
      html += `</table>`;
    }

    // Updated products
    const updatedProducts = stockComparison.filter(c => !c.isNew && !c.willBeRemoved);
    if (updatedProducts.length > 0) {
      html += `<h2>Güncellenecek Ürünler (${updatedProducts.length})</h2>`;
      html += `<table><tr><th>Ürün Kodu</th><th>Ürün Adı</th><th>Mevcut Miktar</th><th>Yeni Miktar</th><th>Değişim</th><th>Mevcut Maliyet</th><th>Yeni Maliyet</th></tr>`;
      updatedProducts.forEach(c => {
        const changeClass = c.quantityChange > 0 ? 'increase' : c.quantityChange < 0 ? 'decrease' : '';
        const changeText = c.quantityChange > 0 ? `+${c.quantityChange}` : c.quantityChange.toString();
        html += `<tr class="updated">
          <td>${c.productCode}</td>
          <td>${c.productName}</td>
          <td>${c.currentQuantity}</td>
          <td>${c.uploadedQuantity}</td>
          <td class="${changeClass}">${changeText}</td>
          <td>${c.currentCost ? '₺' + c.currentCost.toFixed(2) : '-'}</td>
          <td>${c.uploadedCost ? '₺' + c.uploadedCost.toFixed(2) : '-'}</td>
        </tr>`;
      });
      html += `</table>`;
    }

    // Removed products (full wipe mode only)
    if (excelUploadMode === 'clean_slate') {
      const removedProducts = stockComparison.filter(c => c.willBeRemoved);
      if (removedProducts.length > 0) {
        html += `<h2>Sıfırlanacak Ürünler (${removedProducts.length})</h2>`;
        html += `<table><tr><th>Ürün Kodu</th><th>Ürün Adı</th><th>Mevcut Miktar</th><th>Yeni Miktar</th></tr>`;
        removedProducts.forEach(c => {
          html += `<tr class="removed">
            <td>${c.productCode}</td>
            <td>${c.productName}</td>
            <td>${c.currentQuantity}</td>
            <td>0</td>
          </tr>`;
        });
        html += `</table>`;
      }
    }

    html += `</body></html>`;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const saveItemsToDatabase = async (validItems: StockItem[], mode: 'update' | 'clean_slate' = 'update') => {
    // Check and refresh session if needed
    if (!user || !companyId) {
      try {
        if (!isSessionValid()) {
          toast.info('Oturum yenileniyor...');
          await refreshSession();
          // Give the auth context a moment to update
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
        
        if (!user || !companyId) {
          toast.error('Yetkilendirme hatası. Lütfen sayfayı yenileyin veya tekrar giriş yapın.');
          return;
        }
      } catch (error) {
        console.error('Failed to refresh session:', error);
        toast.error('Oturum yenilenemedi. Lütfen tekrar giriş yapın.');
        return;
      }
    }

    // Check for items that need product code (new products)
    const itemsNeedingCode = validItems.filter(item => 
      !item.productCode.trim() && (item.productName.trim() || item.category.trim())
    );

    if (itemsNeedingCode.length > 0) {
      // Prompt for product code for the first item that needs it
      const firstItem = itemsNeedingCode[0];
      handleNewProductPrompt(
        firstItem.id,
        firstItem.productName || '',
        firstItem.category || ''
      );
      toast.info('Yeni ürün için ürün kodu girmeniz gerekiyor');
      return; // Wait for user to provide code
    }

    setSaving(true);
    setShowProgress(true);
    setProgress(0);
    
    try {
      const currentDate = new Date().toISOString();
      const results = {
        created: 0,
        updated: 0,
        errors: 0
      };
      
      const detailedReport = {
        total: validItems.length,
        created: 0,
        updated: 0,
        errors: 0,
        createdItems: [] as StockItem[],
        updatedItems: [] as StockItem[],
        errorItems: [] as { item: StockItem; error: string }[]
      };

      for (let i = 0; i < validItems.length; i++) {
        const item = validItems[i];
        
        // Update progress
        const progressPercent = Math.round((i / validItems.length) * 100);
        setProgress(progressPercent);
        
        try {
          // Check if product exists by item_code with retry logic
          let existingProduct = null;
          let retryCount = 0;
          const maxRetries = 2;
          
          while (retryCount <= maxRetries) {
            try {
              const { data, error } = await supabase
                .from('products')
                .select('id, name, item_code, quantity_on_hand, current_cost')
                .eq('company_id', companyId)
                .eq('item_code', item.productCode)
                .single();
              
              if (error && error.code !== 'PGRST116') { // PGRST116 is "not found" which is expected
                throw error;
              }
              
              existingProduct = data;
              break;
            } catch (error: any) {
              if (error.message?.includes('session') || error.message?.includes('auth')) {
                await refreshSession();
                await new Promise(resolve => setTimeout(resolve, 1000));
                retryCount++;
              } else {
                throw error;
              }
            }
          }

          const quantity = parseInt(item.quantity) || 0;
          const cost = parseFloat(item.cost) || 0;
          const relatedTable = item.sourceEirsaliyeItemId ? 'eirsaliye_item_import' : 'manual_entry';
          const relatedId = item.sourceEirsaliyeItemId || null;

          if (existingProduct) {
            // Update existing product
            let newQuantity: number;
            let quantityChange: number;
            
            if (mode === 'clean_slate') {
              // Full wipe mode: set quantity to Excel value (not add)
              newQuantity = quantity;
              quantityChange = quantity - existingProduct.quantity_on_hand;
            } else {
              // Update mode: add to existing quantity
              newQuantity = existingProduct.quantity_on_hand + quantity;
              quantityChange = quantity;
            }
            
            // Prepare update data
            const updateData: any = {
              quantity_on_hand: newQuantity,
            };
            
            // Only update cost and last_price_update if new cost is provided and is higher than existing cost
            const existingCost = existingProduct.current_cost || 0;
            if (cost > 0 && cost > existingCost) {
              updateData.current_cost = cost;
              updateData.last_price_update = currentDate;
            } else if (cost > 0) {
              // If cost is provided but not higher, still update it (for corrections)
              updateData.current_cost = cost;
            }
            
            const { error: updateError } = await supabase
              .from('products')
              .update(updateData)
              .eq('id', existingProduct.id);

            if (updateError) {
              throw updateError;
            }

            // Log activity
            if (companyId) {
              await logActivity({
                action: 'update',
                entity: 'product',
                entityId: existingProduct.id,
                detail: {
                  name: existingProduct.name,
                  item_code: existingProduct.item_code,
                  changes: updateData,
                  quantity_change: quantityChange,
                  mode: mode,
                },
                locationId: currentLocation?.id || null,
                companyId,
                userId: user?.id || null,
              });
            }

            // Create inventory movement if quantity changed
            if (quantityChange !== 0) {
              const movementType = quantityChange > 0 ? 'purchase_in' : 'adjustment_out';
              const movementQty = Math.abs(quantityChange); // Always store positive qty in movements
              
              const { error: movementError } = await supabase
                .from('inventory_movements')
                .insert({
                  company_id: companyId,
                  product_id: existingProduct.id,
                  movement_type: movementType,
                  qty: movementQty,
                  related_table: relatedTable,
                  related_id: relatedId,
                  actor_user_id: user.id,
                });

              if (movementError) {
                throw movementError;
              }
            }

            results.updated++;
            detailedReport.updated++;
            detailedReport.updatedItems.push(item);
          } else {
            // Create new product
            const productData: ProductInsert = {
              company_id: companyId,
              item_code: item.productCode,
              name: item.productName,
              category: item.category,
              initial_cost: cost, // Always record cost, even if it's 0
              current_cost: cost, // Always record cost, even if it's 0  
              price: 0, // Let users set prices manually later
              quantity_on_hand: quantity,
              entry_date: currentDate,
              meta: {
                createdVia: 'stock_entry',
                brand: ''
              }
            };

            const { data: newProduct, error: insertError } = await supabase
              .from('products')
              .insert(productData)
              .select('id')
              .single();

            if (insertError) {
              throw insertError;
            }

            // Create initial inventory movement if quantity is not 0
            if (quantity !== 0) {
              const movementQty = Math.abs(quantity); // Always store positive qty in movements
              
              const { error: movementError } = await supabase
                .from('inventory_movements')
                .insert({
                  company_id: companyId,
                  product_id: newProduct.id,
                  movement_type: 'initial',
                  qty: movementQty,
                  related_table: relatedTable,
                  related_id: relatedId,
                  actor_user_id: user.id,
                });

              if (movementError) {
                throw movementError;
              }
            }

            // Log activity
            if (companyId) {
              await logActivity({
                action: 'create',
                entity: 'product',
                entityId: newProduct.id,
                detail: {
                  name: item.productName,
                  item_code: item.productCode,
                  category: item.category,
                  quantity: quantity,
                  cost: cost,
                },
                locationId: currentLocation?.id || null,
                companyId,
                userId: user?.id || null,
              });
            }

            results.created++;
            detailedReport.created++;
            detailedReport.createdItems.push(item);
          }
        } catch (itemError) {
          console.error(`Error processing item ${item.productCode}:`, itemError);
          results.errors++;
          detailedReport.errors++;
          detailedReport.errorItems.push({
            item: item,
            error: itemError instanceof Error ? itemError.message : 'Bilinmeyen hata'
          });
        }
      }

      // For full wipe mode, set products not in Excel to 0
      if (mode === 'clean_slate' && companyId) {
        try {
          // Get all products for this company
          const { data: allProducts } = await supabase
            .from('products')
            .select('id, item_code, name, quantity_on_hand')
            .eq('company_id', companyId);
          
          if (allProducts) {
            const excelCodes = new Set(validItems.map(item => item.productCode.toLowerCase()));
            const productsToZero = allProducts.filter(p => 
              p.item_code && !excelCodes.has(p.item_code.toLowerCase()) && (p.quantity_on_hand || 0) > 0
            );
            
            for (const product of productsToZero) {
              const { error: zeroError } = await supabase
                .from('products')
                .update({ quantity_on_hand: 0 })
                .eq('id', product.id);
              
              if (!zeroError && product.quantity_on_hand) {
                // Create inventory movement
                await supabase
                  .from('inventory_movements')
                  .insert({
                    company_id: companyId,
                    product_id: product.id,
                    movement_type: 'adjustment_out',
                    qty: product.quantity_on_hand,
                    related_table: 'manual_entry',
                    related_id: null,
                    actor_user_id: user.id,
                  });
                
                // Log activity
                await logActivity({
                  action: 'update',
                  entity: 'product',
                  entityId: product.id,
                  detail: {
                    name: product.name,
                    item_code: product.item_code,
                    changes: { quantity_on_hand: 0 },
                    quantity_change: -product.quantity_on_hand,
                    mode: 'clean_slate_zero',
                  },
                  locationId: currentLocation?.id || null,
                  companyId,
                  userId: user?.id || null,
                });
              }
            }
          }
        } catch (error) {
          console.error('Error zeroing products in full wipe mode:', error);
        }
      }

      // Set final progress
      setProgress(100);
      
      // Store detailed report
      setUploadReport(detailedReport);
      
      // Show success message
      const messages = [];
      if (results.created > 0) messages.push(`${results.created} yeni ürün oluşturuldu`);
      if (results.updated > 0) messages.push(`${results.updated} ürün güncellendi`);
      if (results.errors > 0) messages.push(`${results.errors} hatalı ürün işlenemedi`);

      if (results.created > 0 || results.updated > 0) {
        toast.success('Stok girişi tamamlandı! ' + messages.join(', '));
        
        // Show detailed report
        setShowReport(true);
        
        // Reset form and comparison
        setStockItems([{
          id: '1',
          productCode: '',
          productName: '',
          category: '',
          cost: '',
          quantity: '',
          searchTerm: '',
          searchTermName: '',
          searchTermCategory: '',
          showSearchResults: false,
          showSearchResultsName: false,
          showSearchResultsCategory: false
        }]);
        setShowPreview(false);
        setShowComparison(false);
        setPendingExcelItems([]);
        setStockComparison([]);
        setValidationSummary({
          total: 0,
          valid: 0,
          errors: 0,
          existing: 0,
          newProducts: 0
        });
      } else {
        toast.error('Hiçbir ürün işlenemedi. Lütfen tekrar deneyin.');
        setShowReport(true); // Show report even for all errors
      }

    } catch (error) {
      console.error('Error saving stock items:', error);
      toast.error('Stok girişi sırasında hata oluştu');
    } finally {
      setSaving(false);
      // Hide progress after a short delay to show completion
      setTimeout(() => {
        setShowProgress(false);
        setProgress(0);
      }, 1000);
    }
  };

  const handleCancelPreview = () => {
    setShowPreview(false);
    setStockItems([{
      id: '1',
      productCode: '',
      productName: '',
      category: '',
      cost: '',
      quantity: '',
      searchTerm: '',
      searchTermName: '',
      searchTermCategory: '',
      showSearchResults: false,
      showSearchResultsName: false,
      showSearchResultsCategory: false
    }]);
    setValidationSummary({
      total: 0,
      valid: 0,
      errors: 0,
      existing: 0,
      newProducts: 0
    });
    setEirsaliyeImportDate(null);
  };

  return (
    <Layout>
      <div className="container mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-h1">Stok Girişi</h1>
            <p className="text-muted-foreground">Yeni ürün stok girişi yapın</p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              onClick={downloadTemplate}
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Şablon İndir
            </Button>
            
            <Button
              variant="outline"
              onClick={downloadAnnualInventoryTemplate}
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Yıllık Sayım Şablonu
            </Button>
            
            <div className="relative">
              <Button
                variant="outline"
                className="flex items-center gap-2"
                onClick={() => setShowModeSelection(true)}
              >
                <Upload className="w-4 h-4" />
                Excel'den Yükle
              </Button>
              <input
                id="excel-upload"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleExcelUpload}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer pointer-events-none"
              />
            </div>
            
            <div className="relative">
              <Button
                variant="outline"
                className="flex items-center gap-2"
                onClick={() => document.getElementById('annual-inventory-upload')?.click()}
              >
                <Upload className="w-4 h-4" />
                Yıllık Sayım Girişi
              </Button>
              <input
                id="annual-inventory-upload"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleAnnualInventoryExcelUpload}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer pointer-events-none"
              />
            </div>
            
            <Button 
              onClick={showPreview ? handleConfirmAndSave : handleSave} 
              disabled={saving || validatingItems || (showPreview && validationSummary.valid === 0)}
              className="flex items-center gap-2"
            >
              {saving ? 'Kaydediliyor...' : showPreview ? 'Onayla ve Kaydet' : 'Kaydet'}
            </Button>
          </div>
        </div>

        {stockItems.some(item => !!item.sourceEirsaliyeItemId) && (
          <Alert className="border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-900">
            <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <AlertDescription className="text-sm text-blue-800 dark:text-blue-200">
              e-Irsaliye import modu aktif.
              {eirsaliyeImportDate ? ` Tarih: ${eirsaliyeImportDate}.` : ''}
              {' '}Maliyetler oncelik sirasiyla Mars {profitSettings.marsPriceTier === 'pesin' ? 'pesin' : `${profitSettings.marsPriceTier} gun`} fiyat, products son maliyet ve 0 olarak getirildi.
            </AlertDescription>
          </Alert>
        )}

        {/* Excel Upload Mode Selection Dialog */}
        <Dialog open={showModeSelection} onOpenChange={setShowModeSelection}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-accent" />
                Excel Yükleme Modu Seçin
              </DialogTitle>
              <DialogDescription>
                Excel dosyasından nasıl veri yüklemek istediğinizi belirleyin.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6">
              <RadioGroup
                value={excelUploadMode}
                onValueChange={(value) => setExcelUploadMode(value as 'update' | 'clean_slate')}
                className="space-y-4"
              >
                <div className="flex items-start space-x-3 p-4 rounded-lg border border-border hover:bg-accent/5">
                  <RadioGroupItem value="update" id="update" className="mt-1" />
                  <div className="flex-1">
                    <Label htmlFor="update" className="flex items-center gap-2 font-medium cursor-pointer">
                      <RefreshCw className="w-4 h-4 text-blue-500" />
                      Güncelleme Modu
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Mevcut ürünleri günceller ve yeni ürünleri ekler. Tablodaki diğer veriler korunur.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3 p-4 rounded-lg border border-border hover:bg-accent/5">
                  <RadioGroupItem value="clean_slate" id="clean_slate" className="mt-1" />
                  <div className="flex-1">
                    <Label htmlFor="clean_slate" className="flex items-center gap-2 font-medium cursor-pointer">
                      <RotateCcw className="w-4 h-4 text-orange-500" />
                      Tam Değiştirme Modu
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Tablodaki tüm verileri temizler ve sadece Excel dosyasındaki verilerle değiştirir.
                    </p>
                  </div>
                </div>
              </RadioGroup>
              
              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowModeSelection(false)}
                >
                  İptal
                </Button>
                <Button
                  onClick={() => {
                    document.getElementById('excel-upload')?.click();
                  }}
                  className="flex items-center gap-2"
                >
                  <Upload className="w-4 h-4" />
                  Dosya Seç
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Validation Loading */}
        {validatingItems && (
          <Card className="glass-panel">
            <CardContent className="pt-6">
              <div className="flex items-center justify-center gap-4 py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-accent" />
                <span className="text-lg">Veriler doğrulanıyor ve mevcut ürünler kontrol ediliyor...</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stock Comparison Preview Section */}
        {showComparison && (
          <Card className="glass-panel border-accent/20">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-accent" />
                  Stok Karşılaştırma Önizleme
                  <span className="text-sm font-normal text-muted-foreground">
                    ({excelUploadMode === 'update' ? 'Güncelleme Modu' : 'Tam Değiştirme Modu'})
                  </span>
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={printComparison}
                    className="flex items-center gap-2"
                  >
                    <Printer className="w-4 h-4" />
                    Farkları Yazdır
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRejectExcel}
                    className="text-destructive hover:bg-destructive/10"
                  >
                    İptal Et
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Major Discrepancies Warning */}
              {(() => {
                const majorDiscrepancies = stockComparison.filter(c => 
                  !c.isNew && 
                  !c.willBeRemoved && 
                  c.quantityChange < 0 && 
                  c.currentQuantity > 0
                );

                return majorDiscrepancies.length > 0 ? (
                  <Card className="border-red-500 bg-red-500/10">
                    <CardHeader>
                      <CardTitle className="text-red-600 flex items-center gap-2">
                        <AlertCircle className="w-5 h-5" />
                        Önemli Uyarı: Stok Farkları Tespit Edildi
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-3">
                        Aşağıdaki <strong>{majorDiscrepancies.length} üründe</strong> mevcut stoktan daha az miktar tespit edildi. 
                        Bu durum stok kaybı veya sayım hatası gösterebilir. Lütfen kontrol ediniz.
                      </p>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {majorDiscrepancies.map((comp, idx) => (
                          <div key={idx} className="p-3 rounded-lg border-2 border-red-500/30 bg-red-500/5">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                              <div>
                                <span className="text-xs text-muted-foreground">Ürün Kodu:</span>
                                <div className="font-bold">{comp.productCode}</div>
                              </div>
                              <div>
                                <span className="text-xs text-muted-foreground">Ürün Adı:</span>
                                <div className="font-medium">{comp.productName}</div>
                              </div>
                              <div>
                                <span className="text-xs text-muted-foreground">Mevcut Stok:</span>
                                <div className="font-bold text-red-600">{comp.currentQuantity} adet</div>
                              </div>
                              <div>
                                <span className="text-xs text-muted-foreground">Excel'deki Miktar:</span>
                                <div className="font-bold text-red-600">{comp.uploadedQuantity} adet</div>
                              </div>
                            </div>
                            <div className="mt-2 pt-2 border-t border-red-500/20">
                              <span className="text-xs font-semibold text-red-600">
                                Fark: {comp.quantityChange} adet (Eksik)
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ) : null;
              })()}

              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <div className="text-2xl font-bold text-blue-500">{stockComparison.length}</div>
                  <div className="text-sm text-muted-foreground">Toplam Ürün</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                  <div className="text-2xl font-bold text-green-500">
                    {stockComparison.filter(c => c.isNew).length}
                  </div>
                  <div className="text-sm text-muted-foreground">Yeni Ürün</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
                  <div className="text-2xl font-bold text-orange-500">
                    {stockComparison.filter(c => !c.isNew && !c.willBeRemoved).length}
                  </div>
                  <div className="text-sm text-muted-foreground">Güncellenecek</div>
                </div>
                {excelUploadMode === 'clean_slate' && (
                  <div className="text-center p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                    <div className="text-2xl font-bold text-red-500">
                      {stockComparison.filter(c => c.willBeRemoved).length}
                    </div>
                    <div className="text-sm text-muted-foreground">Sıfırlanacak</div>
                  </div>
                )}
              </div>

              {/* Comparison List */}
              <div className="space-y-4 max-h-[600px] overflow-y-auto">
                {/* New Products */}
                {stockComparison.filter(c => c.isNew).length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-2 text-green-600 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Yeni Ürünler ({stockComparison.filter(c => c.isNew).length})
                    </h3>
                    <div className="space-y-2">
                      {stockComparison.filter(c => c.isNew).map((comp, idx) => (
                        <div key={idx} className="p-3 rounded-lg border-2 border-green-500/20 bg-green-500/5">
                          <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                            <div>
                              <span className="text-xs text-muted-foreground">Ürün Kodu:</span>
                              <div className="font-medium">{comp.productCode}</div>
                            </div>
                            <div>
                              <span className="text-xs text-muted-foreground">Ürün Adı:</span>
                              <div className="font-medium">{comp.productName}</div>
                            </div>
                            <div>
                              <span className="text-xs text-muted-foreground">Kategori:</span>
                              <div className="font-medium">{comp.category || '-'}</div>
                            </div>
                            <div>
                              <span className="text-xs text-muted-foreground">Miktar:</span>
                              <div className="font-medium text-green-600">{comp.uploadedQuantity}</div>
                            </div>
                            <div>
                              <span className="text-xs text-muted-foreground">Maliyet:</span>
                              <div className="font-medium">{comp.uploadedCost ? `₺${comp.uploadedCost.toFixed(2)}` : '-'}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Updated Products */}
                {stockComparison.filter(c => !c.isNew && !c.willBeRemoved).length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-2 text-orange-600 flex items-center gap-2">
                      <RefreshCw className="w-5 h-5" />
                      Güncellenecek Ürünler ({stockComparison.filter(c => !c.isNew && !c.willBeRemoved).length})
                    </h3>
                    <div className="space-y-2">
                      {stockComparison.filter(c => !c.isNew && !c.willBeRemoved).map((comp, idx) => {
                        const changeClass = comp.quantityChange > 0 ? 'text-green-600' : comp.quantityChange < 0 ? 'text-red-600' : '';
                        const changeText = comp.quantityChange > 0 ? `+${comp.quantityChange}` : comp.quantityChange.toString();
                        const isMajorDiscrepancy = comp.quantityChange < 0 && comp.currentQuantity > 0;
                        return (
                          <div key={idx} className={`p-3 rounded-lg border-2 ${
                            isMajorDiscrepancy 
                              ? 'border-red-500/40 bg-red-500/10' 
                              : 'border-orange-500/20 bg-orange-500/5'
                          }`}>
                            <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
                              <div>
                                <span className="text-xs text-muted-foreground">Ürün Kodu:</span>
                                <div className="font-medium">{comp.productCode}</div>
                              </div>
                              <div>
                                <span className="text-xs text-muted-foreground">Ürün Adı:</span>
                                <div className="font-medium">{comp.productName}</div>
                              </div>
                              <div>
                                <span className="text-xs text-muted-foreground">Mevcut Miktar:</span>
                                <div className="font-medium">{comp.currentQuantity}</div>
                              </div>
                              <div>
                                <span className="text-xs text-muted-foreground">Yeni Miktar:</span>
                                <div className="font-medium">{comp.uploadedQuantity}</div>
                              </div>
                              <div>
                                <span className="text-xs text-muted-foreground">Değişim:</span>
                                <div className={`font-bold ${changeClass}`}>
                                  {changeText}
                                  {isMajorDiscrepancy && (
                                    <span className="ml-2 text-xs bg-red-500 text-white px-2 py-0.5 rounded">ÖNEMLİ</span>
                                  )}
                                </div>
                              </div>
                              <div>
                                <span className="text-xs text-muted-foreground">Mevcut Maliyet:</span>
                                <div className="font-medium">{comp.currentCost ? `₺${comp.currentCost.toFixed(2)}` : '-'}</div>
                              </div>
                              <div>
                                <span className="text-xs text-muted-foreground">Yeni Maliyet:</span>
                                <div className="font-medium">{comp.uploadedCost ? `₺${comp.uploadedCost.toFixed(2)}` : '-'}</div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Removed Products (Full Wipe Mode) */}
                {excelUploadMode === 'clean_slate' && stockComparison.filter(c => c.willBeRemoved).length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-2 text-red-600 flex items-center gap-2">
                      <XCircle className="w-5 h-5" />
                      Sıfırlanacak Ürünler ({stockComparison.filter(c => c.willBeRemoved).length})
                    </h3>
                    <div className="space-y-2">
                      {stockComparison.filter(c => c.willBeRemoved).map((comp, idx) => (
                        <div key={idx} className="p-3 rounded-lg border-2 border-red-500/20 bg-red-500/5">
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                            <div>
                              <span className="text-xs text-muted-foreground">Ürün Kodu:</span>
                              <div className="font-medium">{comp.productCode}</div>
                            </div>
                            <div>
                              <span className="text-xs text-muted-foreground">Ürün Adı:</span>
                              <div className="font-medium">{comp.productName}</div>
                            </div>
                            <div>
                              <span className="text-xs text-muted-foreground">Mevcut Miktar:</span>
                              <div className="font-medium">{comp.currentQuantity}</div>
                            </div>
                            <div>
                              <span className="text-xs text-muted-foreground">Yeni Miktar:</span>
                              <div className="font-medium text-red-600">0</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={handleRejectExcel}
                  className="text-destructive hover:bg-destructive/10"
                >
                  Reddet
                </Button>
                <Button
                  onClick={handleApproveExcel}
                  disabled={saving}
                  className="flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Kaydediliyor...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Onayla ve Kaydet
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Preview Section */}
        {showPreview && (
          <Card className="glass-panel border-accent/20">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-accent" />
                  Excel Yükleme Önizleme
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancelPreview}
                  className="text-destructive hover:bg-destructive/10"
                >
                  İptal Et
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="text-center p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <div className="text-2xl font-bold text-blue-500">{validationSummary.total}</div>
                  <div className="text-sm text-muted-foreground">Toplam</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                  <div className="text-2xl font-bold text-green-500">{validationSummary.valid}</div>
                  <div className="text-sm text-muted-foreground">Geçerli</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                  <div className="text-2xl font-bold text-red-500">{validationSummary.errors}</div>
                  <div className="text-sm text-muted-foreground">Hatalı</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
                  <div className="text-2xl font-bold text-orange-500">{validationSummary.existing}</div>
                  <div className="text-sm text-muted-foreground">Mevcut</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
                  <div className="text-2xl font-bold text-purple-500">{validationSummary.newProducts}</div>
                  <div className="text-sm text-muted-foreground">Yeni</div>
                </div>
              </div>

              {/* Items List */}
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {stockItems.map((item, index) => (
                  <div 
                    key={item.id} 
                    className={`p-4 rounded-lg border-2 ${
                      item.errors && item.errors.length > 0 
                        ? 'border-red-500/20 bg-red-500/5' 
                        : item.isExisting 
                          ? 'border-orange-500/20 bg-orange-500/5'
                          : 'border-green-500/20 bg-green-500/5'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-2">
                        <div>
                          <span className="text-xs text-muted-foreground">Ürün Kodu:</span>
                          <div className="font-medium">{item.productCode}</div>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">Ürün Adı:</span>
                          <div className="font-medium">{item.productName}</div>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">Adet:</span>
                          <div className="font-medium">{item.quantity}</div>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">Maliyet:</span>
                          <div className="font-medium">{item.cost || '-'}</div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {item.errors && item.errors.length > 0 ? (
                          <XCircle className="w-5 h-5 text-red-500" />
                        ) : item.isExisting ? (
                          <AlertCircle className="w-5 h-5 text-orange-500" />
                        ) : (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        )}
                        {item.wasMerged && (
                          <Merge className="w-4 h-4 text-blue-500" />
                        )}
                        <span className="text-sm font-medium">
                          {item.errors && item.errors.length > 0 
                            ? 'Hatalı' 
                            : item.isExisting 
                              ? 'Güncelleme'
                              : 'Yeni'
                          }
                        </span>
                      </div>
                    </div>
                    
                    {/* Error Messages */}
                    {item.errors && item.errors.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {item.errors.map((error, errorIndex) => (
                          <div key={errorIndex} className="text-sm text-red-600 flex items-center gap-2">
                            <XCircle className="w-4 h-4" />
                            {error}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Merged Items Info */}
                    {item.wasMerged && (!item.errors || item.errors.length === 0) && (
                      <div className="mt-2 text-sm text-blue-600 flex items-center gap-2">
                        <Merge className="w-4 h-4" />
                        {item.originalRowCount} adet duplikat satır birleştirildi (toplam: {item.quantity})
                      </div>
                    )}

                    {/* Existing Product Info */}
                    {item.isExisting && (!item.errors || item.errors.length === 0) && (
                      <div className="mt-2 text-sm text-orange-600 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Bu ürün mevcut. Stok miktarı güncellenecek.
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={handleCancelPreview}
                  className="text-destructive hover:bg-destructive/10"
                >
                  İptal Et
                </Button>
                <Button
                  onClick={handleConfirmAndSave}
                  disabled={saving || validationSummary.valid === 0}
                  className="flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Kaydediliyor...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      {validationSummary.valid} Ürünü Onayla ve Kaydet
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stock Entry Form */}
        {!showPreview && (
        <Card className="glass-panel overflow-visible" style={{ position: 'relative', zIndex: 10 }}>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Ürün Listesi</span>
              <Button
                variant="outline"
                size="sm"
                onClick={addNewRow}
                className="flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Satır Ekle
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-visible">
            <div className="space-y-4 overflow-visible">
              {/* Header Row - Hidden on mobile */}
              <div className="hidden md:grid md:grid-cols-12 gap-4 pb-2 border-b border-border">
                <div className="col-span-2">
                  <Label className="text-sm font-medium">Ürün Kodu</Label>
                </div>
                <div className="col-span-3">
                  <Label className="text-sm font-medium">Ürün Adı</Label>
                </div>
                <div className="col-span-2">
                  <Label className="text-sm font-medium">Kategori</Label>
                </div>
                <div className="col-span-2">
                  <Label className="text-sm font-medium">Maliyet</Label>
                </div>
                <div className="col-span-2">
                  <Label className="text-sm font-medium">Adet</Label>
                </div>
                <div className="col-span-1"></div>
              </div>

              {/* Data Rows */}
              {stockItems.map((item) => (
                <div key={item.id} className="space-y-4 md:space-y-0 overflow-visible">
                  {/* Mobile Layout */}
                  <div className="md:hidden glass-panel p-4 rounded-xl space-y-3 overflow-visible">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-sm">Ürün #{stockItems.indexOf(item) + 1}</span>
                      {stockItems.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeRow(item.id)}
                          className="text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div className="relative">
                        <Label className="text-xs text-muted-foreground">Ürün Kodu/Adı</Label>
                        <div className="relative mt-1">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            ref={(el) => {
                              if (el) searchInputRefs.current.set(item.id, el);
                            }}
                            value={item.searchTerm || item.productCode}
                            onChange={(e) => {
                              const value = e.target.value;
                              handleProductSearchChange(item.id, value);
                              if (!item.showSearchResults) {
                                handleFieldChange(item.id, 'productCode', value);
                              }
                            }}
                            onFocus={() => {
                              if (item.searchTerm && item.searchTerm.length >= 2) {
                                setStockItems(prev => prev.map(i => 
                                  i.id === item.id ? { ...i, showSearchResults: true } : i
                                ));
                              }
                            }}
                            placeholder="Ürün ara..."
                            className="pl-10"
                          />
                          {item.showSearchResults && (
                            <div className="absolute top-full left-0 right-0 mt-1 w-full bg-popover border border-border rounded-md shadow-lg max-h-48 overflow-y-auto backdrop-blur-sm" style={{ zIndex: 99999, position: 'absolute' }}>
                              {productSearchLoading.get(item.id) ? (
                                <div className="p-3 text-muted-foreground text-center">
                                  <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                                  Ürünler aranıyor...
                                </div>
                              ) : (productSearchResults.get(item.id) || []).length > 0 ? (
                                (productSearchResults.get(item.id) || []).map((product) => (
                                  <div
                                    key={product.id}
                                    className="flex items-center gap-3 p-3 hover:bg-accent cursor-pointer"
                                    onClick={() => handleProductSelect(item.id, product, 'code')}
                                  >
                                    <Package className="w-8 h-8 text-muted-foreground shrink-0" />
                                    <div className="min-w-0 flex-1">
                                      <p className="font-medium truncate">{product.name}</p>
                                      <p className="text-sm text-muted-foreground truncate">
                                        {product.item_code} • {product.category || 'Kategorisiz'} • ₺{(product.current_cost || product.initial_cost || 0).toLocaleString('tr-TR')}
                                      </p>
                                    </div>
                                  </div>
                                ))
                              ) : (item.searchTerm || '').length >= 2 ? (
                                <div className="p-3 text-muted-foreground text-center">
                                  Ürün bulunamadı
                                </div>
                              ) : (
                                <div className="p-3 text-muted-foreground text-center">
                                  En az 2 karakter girin
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Adet</Label>
                        <Input
                          type="number"
                          min="0"
                          value={item.quantity}
                          onChange={(e) => updateItem(item.id, 'quantity', e.target.value)}
                          placeholder="0"
                          className="mt-1"
                        />
                      </div>
                    </div>
                    
                    <div className="relative">
                      <Label className="text-xs text-muted-foreground">Ürün Adı</Label>
                      <div className="relative mt-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
                        <Input
                          ref={(el) => {
                            if (el) searchNameInputRefs.current.set(item.id, el);
                          }}
                          value={item.searchTermName || item.productName}
                          onChange={(e) => {
                            const value = e.target.value;
                            handleProductNameSearchChange(item.id, value);
                            if (!item.showSearchResultsName) {
                              handleFieldChange(item.id, 'productName', value);
                            }
                          }}
                          onFocus={() => {
                            if (item.searchTermName && item.searchTermName.length >= 2) {
                              setStockItems(prev => prev.map(i => 
                                i.id === item.id ? { ...i, showSearchResultsName: true } : i
                              ));
                            }
                          }}
                          placeholder="Ürün adı ara..."
                          className="pl-10 mt-1"
                        />
                        {item.showSearchResultsName && (
                          <div className="absolute top-full left-0 right-0 mt-1 w-full bg-popover border border-border rounded-md shadow-lg max-h-48 overflow-y-auto backdrop-blur-sm" style={{ zIndex: 99999, position: 'absolute' }}>
                            {productSearchLoading.get(item.id) ? (
                              <div className="p-3 text-muted-foreground text-center">
                                <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                                Ürünler aranıyor...
                              </div>
                            ) : (productSearchResults.get(item.id) || []).length > 0 ? (
                              (productSearchResults.get(item.id) || []).map((product) => (
                                <div
                                  key={product.id}
                                  data-dropdown-item="true"
                                  className="flex items-center gap-3 p-3 hover:bg-accent cursor-pointer"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleProductSelect(item.id, product, 'name', e);
                                  }}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                >
                                  <Package className="w-8 h-8 text-muted-foreground shrink-0" />
                                  <div className="min-w-0 flex-1">
                                    <p className="font-medium truncate">{product.name}</p>
                                    <p className="text-sm text-muted-foreground truncate">
                                      {product.item_code} • {product.category || 'Kategorisiz'} • ₺{(product.current_cost || product.initial_cost || 0).toLocaleString('tr-TR')}
                                    </p>
                                  </div>
                                </div>
                              ))
                            ) : (item.searchTermName || '').length >= 2 ? (
                              <div className="p-3 text-muted-foreground text-center">
                                Ürün bulunamadı
                              </div>
                            ) : (
                              <div className="p-3 text-muted-foreground text-center">
                                En az 2 karakter girin
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div className="relative">
                        <Label className="text-xs text-muted-foreground">Kategori</Label>
                        <div className="relative mt-1">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
                          <Input
                            ref={(el) => {
                              if (el) searchCategoryInputRefs.current.set(item.id, el);
                            }}
                            value={item.searchTermCategory || item.category}
                            onChange={(e) => {
                              const value = e.target.value;
                              handleCategorySearchChange(item.id, value);
                              if (!item.showSearchResultsCategory) {
                                handleFieldChange(item.id, 'category', value);
                              }
                            }}
                            onFocus={() => {
                              if (item.searchTermCategory && item.searchTermCategory.length >= 1) {
                                setStockItems(prev => prev.map(i => 
                                  i.id === item.id ? { ...i, showSearchResultsCategory: true } : i
                                ));
                              } else if (availableCategories.length > 0) {
                                // Show all categories when focused and empty
                                setCategorySearchResults(prev => {
                                  const newMap = new Map(prev);
                                  newMap.set(item.id, availableCategories);
                                  return newMap;
                                });
                                setStockItems(prev => prev.map(i => 
                                  i.id === item.id ? { ...i, showSearchResultsCategory: true } : i
                                ));
                              }
                            }}
                            placeholder="Kategori seç veya yaz..."
                            className="pl-10 mt-1"
                          />
                          {item.showSearchResultsCategory && (
                            <div className="absolute top-full left-0 right-0 mt-1 w-full bg-popover border border-border rounded-md shadow-lg max-h-48 overflow-y-auto backdrop-blur-sm" style={{ zIndex: 99999, position: 'absolute' }}>
                              {categorySearchLoading.get(item.id) ? (
                                <div className="p-3 text-muted-foreground text-center">
                                  <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                                  Kategoriler yükleniyor...
                                </div>
                              ) : (categorySearchResults.get(item.id) || []).length > 0 ? (
                                (categorySearchResults.get(item.id) || []).map((category) => (
                                  <div
                                    key={category}
                                    data-dropdown-item="true"
                                    className="flex items-center gap-3 p-3 hover:bg-accent cursor-pointer"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleCategorySelect(item.id, category, e);
                                    }}
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }}
                                  >
                                    <div className="w-2 h-2 rounded-full bg-accent shrink-0" />
                                    <div className="min-w-0 flex-1">
                                      <p className="font-medium truncate">{category}</p>
                                    </div>
                                  </div>
                                ))
                              ) : (item.searchTermCategory || '').length >= 1 ? (
                                <div className="p-3 text-muted-foreground text-center">
                                  Kategori bulunamadı. Yeni kategori oluşturabilirsiniz.
                                </div>
                              ) : (
                                <div className="p-3 text-muted-foreground text-center">
                                  Kategori yazın veya listeden seçin
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Maliyet</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={item.cost}
                          onChange={(e) => updateItem(item.id, 'cost', e.target.value)}
                          placeholder="0.00"
                          className="mt-1"
                        />
                        {item.costSourceLabel && (
                          <p className="mt-1 text-[11px] text-muted-foreground">
                            Kaynak: {item.costSourceLabel}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Desktop Layout */}
                  <div className="hidden md:grid md:grid-cols-12 gap-4 items-center overflow-visible">
                    <div className="col-span-2 relative">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
                        <Input
                          ref={(el) => {
                            if (el) searchInputRefs.current.set(item.id, el);
                          }}
                          value={item.searchTerm || item.productCode}
                          onChange={(e) => {
                            const value = e.target.value;
                            handleProductSearchChange(item.id, value);
                            if (!item.showSearchResults) {
                              updateItem(item.id, 'productCode', value);
                            }
                          }}
                          onFocus={() => {
                            if (item.searchTerm && item.searchTerm.length >= 2) {
                              setStockItems(prev => prev.map(i => 
                                i.id === item.id ? { ...i, showSearchResults: true } : i
                              ));
                            }
                          }}
                          placeholder="Ürün ara..."
                          className="pl-10"
                        />
                        {item.showSearchResults && (
                          <div className="absolute top-full left-0 right-0 mt-1 w-full bg-popover border border-border rounded-md shadow-lg max-h-48 overflow-y-auto backdrop-blur-sm" style={{ zIndex: 99999, position: 'absolute' }}>
                            {productSearchLoading.get(item.id) ? (
                              <div className="p-3 text-muted-foreground text-center">
                                <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                                Ürünler aranıyor...
                              </div>
                            ) : (productSearchResults.get(item.id) || []).length > 0 ? (
                              (productSearchResults.get(item.id) || []).map((product) => (
                                  <div
                                    key={product.id}
                                    data-dropdown-item="true"
                                    className="flex items-center gap-3 p-3 hover:bg-accent cursor-pointer"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleProductSelect(item.id, product, 'code', e);
                                    }}
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }}
                                  >
                                  <Package className="w-8 h-8 text-muted-foreground shrink-0" />
                                  <div className="min-w-0 flex-1">
                                    <p className="font-medium truncate">{product.name}</p>
                                    <p className="text-sm text-muted-foreground truncate">
                                      {product.item_code} • {product.category || 'Kategorisiz'} • ₺{(product.current_cost || product.initial_cost || 0).toLocaleString('tr-TR')}
                                    </p>
                                  </div>
                                </div>
                              ))
                            ) : (item.searchTerm || '').length >= 2 ? (
                              <div className="p-3 text-muted-foreground text-center">
                                Ürün bulunamadı
                              </div>
                            ) : (
                              <div className="p-3 text-muted-foreground text-center">
                                En az 2 karakter girin
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-span-3 relative">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
                        <Input
                          ref={(el) => {
                            if (el) searchNameInputRefs.current.set(item.id, el);
                          }}
                          value={item.searchTermName || item.productName}
                          onChange={(e) => {
                            const value = e.target.value;
                            handleProductNameSearchChange(item.id, value);
                            if (!item.showSearchResultsName) {
                              handleFieldChange(item.id, 'productName', value);
                            }
                          }}
                          onFocus={() => {
                            if (item.searchTermName && item.searchTermName.length >= 2) {
                              setStockItems(prev => prev.map(i => 
                                i.id === item.id ? { ...i, showSearchResultsName: true } : i
                              ));
                            }
                          }}
                          placeholder="Ürün adı ara..."
                          className="pl-10"
                        />
                        {item.showSearchResultsName && (
                          <div className="absolute top-full left-0 right-0 mt-1 w-full bg-popover border border-border rounded-md shadow-lg max-h-48 overflow-y-auto backdrop-blur-sm" style={{ zIndex: 99999, position: 'absolute' }}>
                            {productSearchLoading.get(item.id) ? (
                              <div className="p-3 text-muted-foreground text-center">
                                <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                                Ürünler aranıyor...
                              </div>
                            ) : (productSearchResults.get(item.id) || []).length > 0 ? (
                              (productSearchResults.get(item.id) || []).map((product) => (
                                <div
                                  key={product.id}
                                  data-dropdown-item="true"
                                  className="flex items-center gap-3 p-3 hover:bg-accent cursor-pointer"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleProductSelect(item.id, product, 'name', e);
                                  }}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                >
                                  <Package className="w-8 h-8 text-muted-foreground shrink-0" />
                                  <div className="min-w-0 flex-1">
                                    <p className="font-medium truncate">{product.name}</p>
                                    <p className="text-sm text-muted-foreground truncate">
                                      {product.item_code} • {product.category || 'Kategorisiz'} • ₺{(product.current_cost || product.initial_cost || 0).toLocaleString('tr-TR')}
                                    </p>
                                  </div>
                                </div>
                              ))
                            ) : (item.searchTermName || '').length >= 2 ? (
                              <div className="p-3 text-muted-foreground text-center">
                                Ürün bulunamadı
                              </div>
                            ) : (
                              <div className="p-3 text-muted-foreground text-center">
                                En az 2 karakter girin
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-span-2 relative">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
                        <Input
                          ref={(el) => {
                            if (el) searchCategoryInputRefs.current.set(item.id, el);
                          }}
                          value={item.searchTermCategory || item.category}
                          onChange={(e) => {
                            const value = e.target.value;
                            handleCategorySearchChange(item.id, value);
                            if (!item.showSearchResultsCategory) {
                              handleFieldChange(item.id, 'category', value);
                            }
                          }}
                          onFocus={() => {
                            if (item.searchTermCategory && item.searchTermCategory.length >= 1) {
                              setStockItems(prev => prev.map(i => 
                                i.id === item.id ? { ...i, showSearchResultsCategory: true } : i
                              ));
                            } else if (availableCategories.length > 0) {
                              // Show all categories when focused and empty
                              setCategorySearchResults(prev => {
                                const newMap = new Map(prev);
                                newMap.set(item.id, availableCategories);
                                return newMap;
                              });
                              setStockItems(prev => prev.map(i => 
                                i.id === item.id ? { ...i, showSearchResultsCategory: true } : i
                              ));
                            }
                          }}
                          placeholder="Kategori seç veya yaz..."
                          className="pl-10"
                        />
                        {item.showSearchResultsCategory && (
                          <div className="absolute top-full left-0 right-0 mt-1 w-full bg-popover border border-border rounded-md shadow-lg max-h-48 overflow-y-auto backdrop-blur-sm" style={{ zIndex: 99999, position: 'absolute' }}>
                            {categorySearchLoading.get(item.id) ? (
                              <div className="p-3 text-muted-foreground text-center">
                                <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                                Kategoriler yükleniyor...
                              </div>
                            ) : (categorySearchResults.get(item.id) || []).length > 0 ? (
                              (categorySearchResults.get(item.id) || []).map((category) => (
                                <div
                                  key={category}
                                  data-dropdown-item="true"
                                  className="flex items-center gap-3 p-3 hover:bg-accent cursor-pointer"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleCategorySelect(item.id, category, e);
                                  }}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                >
                                  <div className="w-2 h-2 rounded-full bg-accent shrink-0" />
                                  <div className="min-w-0 flex-1">
                                    <p className="font-medium truncate">{category}</p>
                                  </div>
                                </div>
                              ))
                            ) : (item.searchTermCategory || '').length >= 1 ? (
                              <div className="p-3 text-muted-foreground text-center">
                                Kategori bulunamadı. Yeni kategori oluşturabilirsiniz.
                              </div>
                            ) : (
                              <div className="p-3 text-muted-foreground text-center">
                                Kategori yazın veya listeden seçin
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-span-2 relative">
                      <Input
                        type="number"
                        step="0.01"
                        value={item.cost}
                        onChange={(e) => updateItem(item.id, 'cost', e.target.value)}
                        placeholder="0.00"
                      />
                      {item.marsPriceSuggestion && (
                        <Alert className="mt-2 p-2 bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
                          <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          <AlertDescription className="text-xs">
                            <div className="flex items-center justify-between gap-2">
                              <span>
                                Mars {item.marsPriceSuggestion.tier === 'pesin' ? 'Peşin' : `${item.marsPriceSuggestion.tier} Gün`} Fiyatı: 
                                <strong className="ml-1">₺{item.marsPriceSuggestion.price.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                                <span className="text-muted-foreground ml-1">
                                  ({new Date(item.marsPriceSuggestion.priceDate).toLocaleDateString('tr-TR', { month: 'short', day: 'numeric' })})
                                </span>
                              </span>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUseMarsPrice(item.id)}
                                className="h-6 text-xs"
                              >
                                Kullan
                              </Button>
                            </div>
                          </AlertDescription>
                        </Alert>
                      )}
                      {item.costSourceLabel && (
                        <p className="mt-1 text-[11px] text-muted-foreground">
                          Kaynak: {item.costSourceLabel}
                        </p>
                      )}
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        min="0"
                        value={item.quantity}
                        onChange={(e) => updateItem(item.id, 'quantity', e.target.value)}
                        placeholder="0"
                      />
                    </div>
                    <div className="col-span-1 flex justify-center">
                      {stockItems.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeRow(item.id)}
                          className="text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        )}

        {/* Summary */}
        {!showPreview && (
        <Card className="glass-panel" style={{ position: 'relative', zIndex: 1 }}>
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Toplam Ürün Çeşidi</p>
                <p className="text-h3 font-bold">{stockItems.filter(item => item.productCode || item.productName).length}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Toplam Adet</p>
                <p className="text-h3 font-bold">
                  {stockItems.reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Toplam Maliyet</p>
                <p className="text-h3 font-bold">
                  ₺{stockItems.reduce((sum, item) => sum + ((parseFloat(item.cost) || 0) * (parseInt(item.quantity) || 0)), 0).toFixed(2)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        )}
      </div>

      {/* Progress Dialog */}
      {showProgress && (
        <Dialog open={showProgress} onOpenChange={() => {}}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Stok Girişi İşleniyor</DialogTitle>
              <DialogDescription>
                Ürünler veritabanına kaydediliyor...
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <Progress value={progress} className="w-full" />
              <p className="text-sm text-center text-muted-foreground">
                %{progress} tamamlandı
              </p>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Upload Report Dialog */}
      {showReport && (
        <Dialog open={showReport} onOpenChange={setShowReport}>
          <DialogContent className="max-w-4xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                Stok Girişi Raporu
                <Button variant="ghost" size="sm" onClick={() => setShowReport(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </DialogTitle>
              <DialogDescription>
                İşlem detayları ve sonuçları
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">{uploadReport.total}</div>
                    <div className="text-sm text-muted-foreground">Toplam</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-green-600">{uploadReport.created}</div>
                    <div className="text-sm text-muted-foreground">Yeni Oluşturulan</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-orange-600">{uploadReport.updated}</div>
                    <div className="text-sm text-muted-foreground">Güncellenen</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-red-600">{uploadReport.errors}</div>
                    <div className="text-sm text-muted-foreground">Hatalı</div>
                  </CardContent>
                </Card>
              </div>

              {/* Created Items */}
              {uploadReport.createdItems.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-green-600 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Yeni Oluşturulan Ürünler ({uploadReport.createdItems.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {uploadReport.createdItems.map((item, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-green-50 rounded-md">
                          <div>
                            <div className="font-medium">{item.productName}</div>
                            <div className="text-sm text-muted-foreground">
                              {item.productCode} • {item.quantity} adet • ₺{item.cost}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Updated Items */}
              {uploadReport.updatedItems.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-orange-600 flex items-center gap-2">
                      <RefreshCw className="w-5 h-5" />
                      Güncellenen Ürünler ({uploadReport.updatedItems.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {uploadReport.updatedItems.map((item, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-orange-50 rounded-md">
                          <div>
                            <div className="font-medium">{item.productName}</div>
                            <div className="text-sm text-muted-foreground">
                              {item.productCode} • +{item.quantity} adet • ₺{item.cost}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Error Items */}
              {uploadReport.errorItems.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-red-600 flex items-center gap-2">
                      <XCircle className="w-5 h-5" />
                      Hatalı Ürünler ({uploadReport.errorItems.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {uploadReport.errorItems.map((errorItem, index) => (
                        <div key={index} className="p-3 bg-red-50 border border-red-200 rounded-md">
                          <div className="font-medium text-red-800">{errorItem.item.productName}</div>
                          <div className="text-sm text-muted-foreground">
                            {errorItem.item.productCode} • {errorItem.item.quantity} adet • ₺{errorItem.item.cost}
                          </div>
                          <div className="text-sm text-red-600 mt-1 font-medium">
                            Hata: {errorItem.error}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* New Product Code Dialog */}
      <Dialog open={showNewProductDialog} onOpenChange={setShowNewProductDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Yeni Ürün Kodu Gerekli</DialogTitle>
            <DialogDescription>
              Bu yeni bir ürün görünüyor. Lütfen ürün kodu girin.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {pendingNewProduct && (
              <div className="space-y-2">
                <div>
                  <Label className="text-sm font-medium">Ürün Adı</Label>
                  <p className="text-sm text-muted-foreground">{pendingNewProduct.productName || '-'}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Kategori</Label>
                  <p className="text-sm text-muted-foreground">{pendingNewProduct.category || '-'}</p>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="new-product-code">Ürün Kodu *</Label>
              <Input
                id="new-product-code"
                value={newProductCode}
                onChange={(e) => setNewProductCode(e.target.value)}
                placeholder="Ürün kodunu girin..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newProductCode.trim()) {
                    handleConfirmNewProduct();
                  }
                }}
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowNewProductDialog(false);
                  setPendingNewProduct(null);
                  setNewProductCode('');
                }}
              >
                İptal
              </Button>
              <Button
                onClick={handleConfirmNewProduct}
                disabled={!newProductCode.trim()}
              >
                Onayla ve Kaydet
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </Layout>
  );
};

export default StockEntry;